import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

interface Message {
  _id: string;
  sender: string;
  senderType: 'Customer' | 'Caregiver';
  content: string;
  type: 'text' | 'pin' | 'system';
  createdAt: string;
  visitId: string;
}

interface ChatInterfaceProps {
  jobId: string;
  visitId: string;
  currentUserType: 'customer' | 'caregiver';
  className?: string;
  hasActiveSafetyCheck?: boolean;
  onPinClick?: (pin: string) => void;
  title?: string;
  onClose?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  jobId, 
  visitId, 
  currentUserType,
  className = '',
  hasActiveSafetyCheck = false,
  onPinClick,
  title = "Live Chat",
  onClose
}) => {
  const { socket } = useNotification();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sharingPin, setSharingPin] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Poll for messages every 3 seconds
  // REPLACED BY SOCKET - but kept as fallback for safety if needed, or removed entirely
  // Keeping active polling just in case socket fails is safer for MVP, but slowing it down
  useEffect(() => {
    fetchMessages();
    const interval = setInterval(() => {
      // Only poll if tab is active
      if (!document.hidden) {
        fetchMessages();
      }
    }, 15000); // More relaxed polling
    return () => clearInterval(interval);
  }, [jobId, visitId]);

  // Socket listener
  useEffect(() => {
    if (!socket || !jobId) return;

    socket.emit('join_job', jobId);

    socket.on('new_message', (msg: Message) => {
        // Only add if belongs to this visit - although roomId is jobId, chat is by visitId filtered
        if (msg.visitId === visitId) {
            setMessages(prev => [...prev, msg]);
        }
    });

    return () => {
        socket.emit('leave_job', jobId);
        socket.off('new_message');
    }
  }, [socket, jobId, visitId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/chat/${jobId}/${visitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        // Only update if length changed to unexpected jumps, 
        // but for a simple chat, replacing is usually fine unless user is scrolling up.
        // For simplicity, we just set state.
        setMessages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching messages', error);
    }
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
        const token = localStorage.getItem('token');
        await axios.post('/api/chat/send', {
            jobId,
            visitId,
            content: newMessage
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setNewMessage('');
        fetchMessages(); // Immediate fetch
    } catch (error) {
        console.error('Error sending message', error);
        alert('Failed to send message');
    } finally {
        setSending(false);
    }
  };

  const handleSharePin = async () => {
    if (sharingPin) return;
    
    // confirm dialog
    if (!window.confirm("Are you sure you want to share the active safety PIN with the caregiver?")) {
        return;
    }

    setSharingPin(true);
    try {
        const token = localStorage.getItem('token');
        await axios.post('/api/chat/share-pin', {
            jobId,
            visitId
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        fetchMessages();
    } catch (error: any) {
        console.error('Error sharing PIN', error);
        alert(error.response?.data?.message || 'Failed to share PIN. Make sure a safety check is currently pending.');
    } finally {
        setSharingPin(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`flex flex-col h-[500px] bg-gray-50 rounded-xl border border-gray-200 shadow-sm ${className}`}>
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-primary-600 to-red-600 p-4 rounded-t-xl flex justify-between items-center z-10 sticky top-0 text-white shadow-md">
        <h3 className="font-bold text-white flex items-center gap-2 truncate max-w-[60%]">
            <div className="relative shrink-0">
                <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500 border-2 border-white"></span>
            </div>
            <span className="truncate">{title}</span>
        </h3>
        
        <div className="flex items-center gap-2">
            {/* Customer Action: Share PIN */}
            {currentUserType === 'customer' && hasActiveSafetyCheck && (
                <button 
                    onClick={handleSharePin}
                    disabled={sharingPin}
                    className="text-xs bg-white text-primary-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors font-bold shadow-sm flex items-center gap-1 shrink-0"
                    title="Share active safety PIN"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Send PIN
                </button>
            )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={chatContainerRef}>
        {messages.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-10">
                <p>No messages yet.</p>
                <p className="text-xs mt-1">Chat history is saved for this shift.</p>
            </div>
        ) : (
            messages.map((msg) => {
                const isMe = (currentUserType === 'customer' && msg.senderType === 'Customer') || 
                             (currentUserType === 'caregiver' && msg.senderType === 'Caregiver');
                
                return (
                    <div key={msg._id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div 
                            className={`max-w-[80%] rounded-2xl p-3 shadow-sm relative ${
                                msg.type === 'pin' 
                                    ? 'bg-amber-50 border border-amber-200 text-amber-900 rounded-br-none' 
                                    : isMe 
                                        ? 'bg-primary-600 text-white rounded-br-none' 
                                        : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                            }`}
                        >
                            {/* Message Content */}
                            {msg.type === 'pin' ? (
                                <div 
                                    onClick={() => onPinClick && onPinClick(msg.content)}
                                    className={`text-center py-1 px-2 w-full ${onPinClick ? 'cursor-pointer hover:bg-amber-100/50 rounded transition-colors active:scale-95 group' : ''}`}
                                    title={onPinClick ? "Tap to fill PIN" : ""}
                                >
                                    <p className="text-xs uppercase tracking-wide opacity-70 mb-1">Safety PIN Shared</p>
                                    <p className="text-2xl font-mono font-bold tracking-widest">{msg.content}</p>
                                    {onPinClick && (
                                        <p className="text-[10px] text-primary-600 font-medium mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            Tap to fill
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                            )}
                            
                            {/* Metadata */}
                            <div className={`text-[10px] mt-1 text-right ${
                                msg.type === 'pin' ? 'text-amber-800/60' : isMe ? 'text-primary-100' : 'text-gray-400'
                            }`}>
                                {!isMe && <span className="mr-1 font-medium">{msg.senderType} •</span>}
                                {formatTime(msg.createdAt)}
                            </div>
                        </div>
                    </div>
                );
            })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={handleSendMessage} className="p-3 bg-white border-t border-gray-200 rounded-b-xl flex gap-2">
        <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-primary-600 text-white p-2 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
};

export default ChatInterface;
